import React from "react";
import ReactApexChart from "react-apexcharts";
import { T, FONT_SANS } from "../../constants";
import { hasCapacity, WARNING_HEADROOM } from "../forecastSummary";
import { formatDayAxis, formatDayLong } from "../forecastRange";

const MAX_AXIS_LABELS = 14;

export const CHART_HEIGHT = 220;

const tooltipRow = (color, label, value) =>
  `<div style="display:flex;gap:8px;align-items:center;color:${T.mutedFg};margin-bottom:3px;">` +
    `<span style="width:8px;height:8px;border-radius:2px;background:${color};display:inline-block;"></span>` +
    `<span style="font-size:13px;">${label}</span>` +
    `<span style="font-weight:600;color:${T.fg};margin-left:auto;font-family:monospace;font-size:13px;">${value}</span>` +
  `</div>`;

export default function ForecastPlot({
  days,
  capacity,
  todayDate,
  selectedDate,
  onSelectDate,
}) {
  const categories = days.map((d) => d.date);
  const arrivals = days.map((d) => d.arrivals);
  const departures = days.map((d) => d.departures);
  const occupancy = days.map((d) => d.occupancy);
  const maxCount = Math.max(1, ...arrivals, ...departures);
  const capped = hasCapacity(capacity);

  const countTicks = Math.min(5, maxCount);
  const countAxisMax = Math.ceil(maxCount / countTicks) * countTicks;

  const peak = Math.max(0, ...occupancy);
  const occAxisMax = Math.max(5, Math.ceil((Math.max(capped ? capacity : 0, peak) * 1.12) / 5) * 5);

  const labelStride = Math.ceil(categories.length / MAX_AXIS_LABELS) || 1;
  const labelledDates = new Set(categories.filter((_, i) => i % labelStride === 0));

  const select = (cfg) => {
    const i = cfg && cfg.dataPointIndex;
    if (typeof i !== "number" || i < 0 || !categories[i]) return;
    onSelectDate(categories[i]);
  };

  const discreteMarkers = capped
    ? occupancy
        .map((o, i) => {
          const over = o >= capacity;
          const warn = !over && o >= capacity - WARNING_HEADROOM;
          if (!over && !warn) return null;
          const color = over ? T.danger : T.warningSolid;
          return { seriesIndex: 2, dataPointIndex: i, fillColor: color, strokeColor: color, size: 4 };
        })
        .filter(Boolean)
    : [];

  const series = [
    { name: "Arrivals", type: "column", data: arrivals },
    { name: "Departures", type: "column", data: departures },
    { name: "Projected occupancy", type: "line", data: occupancy },
  ];

  const xannos = [];
  if (todayDate && categories.includes(todayDate)) {
    xannos.push({
      x: todayDate,
      strokeDashArray: 3,
      borderColor: T.primary,
      opacity: 0.5,
      label: {
        text: "today",
        position: "top",
        orientation: "horizontal",
        style: { color: "#fff", background: T.primary, fontSize: "10px" },
      },
    });
  }
  if (selectedDate && categories.includes(selectedDate)) {
    xannos.push({ x: selectedDate, strokeDashArray: 0, borderColor: T.primary, opacity: 0.9, borderWidth: 2 });
  }

  const yannos = capped
    ? [
        {
          y: Math.max(0, capacity - WARNING_HEADROOM),
          y2: capacity,
          yAxisIndex: 2,
          fillColor: T.warningSolid,
          opacity: 0.15,
          borderColor: "transparent",
        },
        {
          y: capacity,
          yAxisIndex: 2,
          strokeDashArray: 3,
          borderColor: T.danger,
          label: {
            text: `Capacity ${capacity}`,
            position: "left",
            textAnchor: "start",
            offsetX: 6,
            borderColor: "transparent",
            style: { color: T.danger, background: "transparent", fontSize: "10px" },
          },
        },
      ]
    : [];

  const options = {
    chart: {
      type: "line",
      fontFamily: FONT_SANS,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
      parentHeightOffset: 0,
      events: {
        dataPointSelection: (e, ctx, cfg) => select(cfg),
      },
    },
    colors: [T.success, T.primary, T.fg],
    stroke: { width: [0, 0, 2], curve: "smooth" },
    plotOptions: { bar: { columnWidth: "62%", borderRadius: 2 } },
    dataLabels: { enabled: false },
    fill: { opacity: [0.9, 0.9, 1] },
    markers: { size: [0, 0, 3], colors: [T.fg], strokeWidth: 0, discrete: discreteMarkers, hover: { sizeOffset: 2 } },
    legend: { show: false },
    states: { active: { filter: { type: "none" } } },
    grid: { borderColor: T.borderSoft, strokeDashArray: 3, padding: { top: 0, right: 8, left: 4, bottom: 0 } },
    xaxis: {
      categories,
      labels: {
        rotate: 0,
        hideOverlappingLabels: false,
        style: { colors: T.mutedFg, fontSize: "11px" },
        formatter: (v) => (labelledDates.has(v) ? formatDayAxis(v) : ""),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: [
      {
        seriesName: "Arrivals",
        opposite: true,
        min: 0,
        max: countAxisMax,
        tickAmount: countTicks,
        title: { text: "Movements", style: { color: T.mutedFg, fontSize: "11px", fontWeight: 500 } },
        labels: { style: { colors: T.mutedFg, fontSize: "11px" }, formatter: (v) => Math.round(v) },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      {
        seriesName: "Arrivals",
        opposite: true,
        min: 0,
        max: countAxisMax,
        tickAmount: countTicks,
        show: false,
      },
      {
        seriesName: "Projected occupancy",
        min: 0,
        max: occAxisMax,
        title: { text: "Occupancy", style: { color: T.mutedFg, fontSize: "11px", fontWeight: 500 } },
        labels: { style: { colors: T.mutedFg, fontSize: "11px" }, formatter: (v) => Math.round(v) },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
    ],
    annotations: { yaxis: yannos, xaxis: xannos },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }) => {
        const date = categories[dataPointIndex];
        if (!date) return "";
        const a = arrivals[dataPointIndex] || 0;
        const d = departures[dataPointIndex] || 0;
        const o = occupancy[dataPointIndex] || 0;
        return (
          `<div style="background:#fff;border:1px solid ${T.border};border-radius:10px;padding:10px 14px;box-shadow:0 4px 16px rgba(0,0,0,0.08);">` +
            `<div style="font-weight:600;margin-bottom:6px;color:${T.fg};font-size:13px;">${formatDayLong(date)}</div>` +
            tooltipRow(T.success, "Arrivals", a) +
            tooltipRow(T.primary, "Departures", d) +
            tooltipRow(T.fg, "Projected", capped ? `${o}/${capacity}` : `${o}`) +
          `</div>`
        );
      },
    },
  };

  return (
    <ReactApexChart options={options} series={series} type="line" height={CHART_HEIGHT} width="100%" />
  );
}
